import { Menu, Search, type MenuItem } from 'platform/components';
import { CategoryColorScheme } from '../../../../../../constants/categoryColors';
import { buildCategoryItems, type CategoryItem } from '../../../../../../hooks/useFilterCategorySelection';
import type { EagColumnOrGroup } from '../../../../../../types/EagColumnGroup';
import type { FilterState } from '../../../../../../types/FilterState';
type FilterCategoryMenuProps = {
  columnConfig: EagColumnOrGroup[];
  filterState: FilterState;
  selectedCategoryId: string | null;
  onCategorySelect: (categoryId: string) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
};

// Re-export so existing consumers of FilterCategoryMenu's exports keep working.
export type { CategoryItem };
export { buildCategoryItems };
const toBadgeColorScheme = (color: CategoryColorScheme) => color as 'blue' | 'orange' | 'green' | 'purple' | 'teal' | 'red' | 'magenta' | 'yellow' | 'neutral';

/**
 * Left sidebar category list for the desktop 75vw FilterPanel.
 *
 * Uses the platform `Menu` component for rendering (consistent design system look),
 * with a custom search input placed above it.  The `Menu` is NOT rendered with
 * `isSearchable` because platform `Menu` only filters by item label — it cannot
 * search inside a category's filter children.  Instead this component owns the
 * search state and pre-filters the items list before passing it to `Menu`.
 *
 * ## Cross-category search
 *
 * When `searchTerm` is non-empty, a category is shown if:
 * 1. Its own label contains the search term (category name match), OR
 * 2. Any of its filterable children's `headerName` values contain the search term.
 *
 * Searching "External Id" will surface "General" (or whatever category contains
 * the External Id filter) even though the category label itself doesn't match.
 *
 * The same `searchTerm` is forwarded to `FilterContentArea` so filter widgets
 * within the selected category are also narrowed down.
 *
 * ## Why no store reads here
 *
 * `filterState`, `selectedCategoryId`, and `searchTerm` are all props from
 * `FilterPanel`, keeping this component pure/presentational and store-agnostic.
 */
export function FilterCategoryMenu(props: FilterCategoryMenuProps) {
  const allItems = buildCategoryItems(props.columnConfig, props.filterState);
  const lowerSearch = props.searchTerm.toLowerCase();
  const visibleItems = lowerSearch ? allItems.filter(item => item.label.toLowerCase().includes(lowerSearch) || item.childLabels.some(label => label.includes(lowerSearch))) : allItems;
  const menuItems: MenuItem[] = visibleItems.map(item => ({
    id: item.id,
    label: item.label,
    badge: item.activeCount > 0 ? {
      value: item.activeCount,
      colorScheme: toBadgeColorScheme(item.color)
    } : undefined
  }));
  return <div className="flex flex-col h-full overflow-hidden">
      {/* Custom search — replaces Menu's built-in isSearchable so we can match
          against filter children's labels, not just category labels.
          Uses platform Search component for visual consistency with Menu. */}
      <div className="px-3 pt-3 pb-2 flex-shrink-0">
        <Search value={props.searchTerm || null} onChange={value => props.onSearchChange(value ?? '')} data-testid="filter-category-search" />
      </div>

      {/* Platform Menu — receives pre-filtered items, no isSearchable */}
      <div className="flex-1 overflow-y-auto px-2 py-1">
        <Menu items={menuItems} activeItemId={props.selectedCategoryId ?? undefined} onItemChange={props.onCategorySelect} data-testid="filter-category-menu" />
      </div>
    </div>;
}